"use client";

import { useState, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { Building2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ROLES, type RoleId } from "@/lib/constants/roles";
import { useAuthAndPermissions, useVenues } from "@/contexts/auth-and-permissions-context";
import { trackPixelEvent } from "@/app/actions/track-pixel";

interface Venue {
  id: number | string; // Can be number from API or string from props
  name: string;
  currency?: string;
  address?: string;
}

interface VenueSwitcherProps {
  selectedVenueId?: string | null;
  onVenueChange?: (venue: Venue | null) => void;
  portalBase?: string;
  showGlobalOption?: boolean;
}

export default function VenueSwitcher({
  selectedVenueId,
  onVenueChange,
  portalBase = "/superadmin",
  showGlobalOption = false,
}: VenueSwitcherProps) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRoleId, setUserRoleId] = useState<RoleId | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const { selectedVenue, setSelectedVenue, user } = useAuthAndPermissions();
  const { availableVenues } = useVenues();

  // Extract venue ID from pathname if not provided as prop
  const venueMatch = pathname.match(
    /\/(?:admin|superadmin|booking|barstaff)\/venue\/(\d+)/,
  );
  const venueIdFromPath = venueMatch ? venueMatch[1] : null;

  // Fetch venues function that can be called on demand
  const fetchVenues = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/context/selectable-venues");

      if (!response.ok) {
        const text = await response.text();
        console.error(
          "Venue fetch failed. Response:",
          text.substring(0, 200),
        );
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.venues) {
        setVenues(data.venues);
        setUserRoleId(data.userRoleId || null);
      }
    } catch (error) {
      console.error("Failed to fetch venues:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load - only fetch once on component mount
  useEffect(() => {
    // For super admin, always fetch fresh from database
    if (user?.role_id === ROLES.SUPER_ADMIN) {
      fetchVenues();
    }
    // For other users, use context if available
    else if (availableVenues && availableVenues.length > 0) {
      setVenues(availableVenues);
      setUserRoleId(user?.role_id || null);
      setLoading(false);
    }
    // Otherwise fetch from API
    else {
      fetchVenues();
    }
  }, []); // Empty dependency array - only run once on mount

  // The URL is the source of truth for the current venue. Keep the global
  // selection in sync so a stale stored venue (e.g. the auto-selected primary
  // venue) doesn't shadow it - otherwise picking that venue is a no-op.
  useEffect(() => {
    if (!venueIdFromPath || String(selectedVenue?.id) === venueIdFromPath) {
      return;
    }
    const venueFromPath = venues.find((v) => String(v.id) === venueIdFromPath);
    if (venueFromPath) {
      setSelectedVenue({
        id: Number(venueFromPath.id),
        name: venueFromPath.name,
        currency: venueFromPath.currency || "USD",
        address: venueFromPath.address,
      });
    }
  }, [venueIdFromPath, venues, selectedVenue?.id]);

  const handleVenueSelect = (value: string) => {
    let selectedVenueData: Venue | null = null;

    if (value !== "global") {
      selectedVenueData = venues.find((v) => String(v.id) === value) || null;
    }

    // Update global state - ensure we have the required fields
    if (selectedVenueData) {
      setSelectedVenue({
        id:
          typeof selectedVenueData.id === "string"
            ? parseInt(selectedVenueData.id)
            : selectedVenueData.id,
        name: selectedVenueData.name,
        currency: selectedVenueData.currency || "USD",
        address: selectedVenueData.address,
      });
    } else {
      setSelectedVenue(null);
    }

    // Call the callback if provided
    if (onVenueChange) {
      onVenueChange(selectedVenueData);
    }

    // Track pixel event when venue is selected
    if (selectedVenueData && value !== "global") {
      const venueId = typeof selectedVenueData.id === "string"
        ? parseInt(selectedVenueData.id)
        : selectedVenueData.id;

      trackPixelEvent({
        venueId,
        eventType: "PageView",
        eventData: {
          content_name: `${selectedVenueData.name} - Venue Selected`,
          content_type: "venue",
          custom_fields: {
            venue_name: selectedVenueData.name,
            venue_id: venueId,
          },
        },
      }).catch((error) => {
        // Log pixel tracking errors but don't block venue selection
        if (process.env.NODE_ENV === "development") {
          console.debug("[FacebookPixel] Venue selection tracking failed:", error);
        }
      });
    }

    // Navigate to the appropriate route
    if (value === "global") {
      router.push(`${portalBase}/dashboard`);
    } else {
      // Maintain current page context when switching venues, but redirect to parent page for specific items
      const currentPath =
        pathname.split(`/venue/${venueIdFromPath}/`)[1] || "dashboard";

      // If we're on a specific show page, redirect to the shows list instead
      if (currentPath.match(/^shows\/\d+/)) {
        router.push(`${portalBase}/venue/${value}/shows`);
      }
      // If we're on a specific booking page, redirect to the bookings list instead
      else if (currentPath.match(/^bookings\/\d+/)) {
        router.push(`${portalBase}/venue/${value}/bookings`);
      } else {
        router.push(`${portalBase}/venue/${value}/${currentPath}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="w-[220px]">
        <Select disabled value="" onValueChange={() => {}}>
          <SelectTrigger className="w-full h-10">
            <div className="flex items-center gap-2 pr-8">
              <Building2 className="h-4 w-4 flex-shrink-0" />
              <span>Loading...</span>
            </div>
          </SelectTrigger>
        </Select>
      </div>
    );
  }

  // Determine if we're on a global/non-venue-specific page
  // Check for both /venue/ (venue-specific pages) and /venues/[id] (venue edit pages)
  const isGlobalPage =
    !pathname.includes("/venue/") && !pathname.match(/\/venues\/\d+/);

  // The venue in the URL wins; fall back to the global selection for pages
  // like /superadmin/venues/[id]. On global pages always show "global".
  const currentValue = isGlobalPage
    ? "global"
    : venueIdFromPath ||
      (selectedVenue?.id ? String(selectedVenue.id) : null) ||
      selectedVenueId ||
      "global";

  // Find the current venue name - handle both string and number comparisons
  const currentVenueName =
    currentValue === "global"
      ? "All venues / Global"
      : venues.find((v) => String(v.id) === String(currentValue))?.name ||
        (String(selectedVenue?.id) === currentValue
          ? selectedVenue?.name
          : undefined) ||
        "Select venue";

  return (
    <div className="w-[220px]">
      <Select
        value={String(currentValue)}
        onValueChange={handleVenueSelect}
        onOpenChange={(open) => {
          // For super admin only: fetch fresh venues when dropdown is opened
          if (open && userRoleId === ROLES.SUPER_ADMIN) {
            fetchVenues();
          }
        }}
      >
        <SelectTrigger className="w-full h-10">
          <div className="flex items-center gap-2 pr-8 overflow-hidden">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate flex-1 text-left">
              {currentVenueName}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent className="w-[220px]" align="start" sideOffset={4}>
          {/* Global/All Venues option for Super Admins */}
          {(userRoleId === ROLES.SUPER_ADMIN || showGlobalOption) && (
            <SelectItem value="global">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">All venues / Global</span>
              </div>
            </SelectItem>
          )}

          {/* Individual venues */}
          {venues.map((venue) => (
            <SelectItem key={venue.id} value={String(venue.id)}>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{venue.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
